import React from 'react';
import {
  ReserveConfirmOutlineBox,
  ReserveConfirmOutlineBoxTitle
} from '@this/components/reserve_trip/reserve_confirm/outline/shared';
import type BufferAmountSetting from '@this/domain/organization/buffer_amount_setting';
import Transit from '@this/domain/transit/transit';
import Flight from '@this/domain/flight/flight';
import Hotel from '@this/domain/hotel/hotel';
import type ReservingTrip from '@this/domain/trip/reserving_trip';
import type ReservingTripItem from '@this/domain/trip/reserving_trip_item';

type BufferAmountsSectionProps = {
  reservingTrip: ReservingTrip;
  bufferAmountSetting: BufferAmountSetting;
};

type BufferItemData = {
  key: string;
  title: string;
  bufferAmountText: string;
};

const styles = {
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '5px'
  },
  container: {
    padding: '10px',
    fontWeight: 'bold' as const,
    color: '#959595'
  }
};

const BufferAmountsSection: React.FC<BufferAmountsSectionProps> = ({ reservingTrip, bufferAmountSetting }) => {
  const createTransitBufferItem = (item: ReservingTripItem) => {
    if (
      !(item.element instanceof Transit) ||
      !item.element.airExists() ||
      item.totalBufferAmount(bufferAmountSetting) <= 0
    ) {
      return null;
    }

    return {
      key: `transit-${item.element.id}`,
      title: '国内航空券',
      bufferAmountText: item.element.bufferAmountText(bufferAmountSetting)
    };
  };

  const createFlightBufferItem = (item: ReservingTripItem) => {
    if (!(item.element instanceof Flight) || item.totalBufferAmount(bufferAmountSetting) <= 0) {
      return null;
    }

    return {
      key: `flight-${item.element.id}`,
      title: '海外航空券',
      bufferAmountText: item.element.bufferAmountText(bufferAmountSetting)
    };
  };

  const createHotelBufferItem = (item: ReservingTripItem) => {
    if (!(item.element instanceof Hotel) || item.totalBufferAmount(bufferAmountSetting) <= 0) {
      return null;
    }

    const hotelType = item.element.isForeign() ? '海外ホテル' : '国内ホテル';
    const title = `${hotelType}${item.isHotelPriceExceeded() ? ' (規定料金超過)' : ''}`;

    return {
      key: `hotel-${item.element.id}`,
      title,
      bufferAmountText: item.element.bufferAmountText(
        bufferAmountSetting,
        item.isHotelPriceExceeded(),
        item.hotelPriceLimit,
        item.element.getAveragePriceWithTax() || 0
      )
    };
  };

  // 表示用データを事前に整形
  const bufferItems = reservingTrip.items
    .filter(item => item !== undefined)
    .flatMap(item => {
      const results: BufferItemData[] = [];

      const transitItem = createTransitBufferItem(item);
      if (transitItem) results.push(transitItem);

      const flightItem = createFlightBufferItem(item);
      if (flightItem) results.push(flightItem);

      const hotelItem = createHotelBufferItem(item);
      if (hotelItem) results.push(hotelItem);

      return results;
    });

  return (
    <ReserveConfirmOutlineBox>
      <ReserveConfirmOutlineBoxTitle>各商品の許容幅</ReserveConfirmOutlineBoxTitle>
      <div style={styles.container}>
        {reservingTrip.isPackage() ? (
          <div style={styles.item}>
            <p>パッケージ</p>
            <p>{`${utils.digits(bufferAmountSetting.getPackageBufferAmount())}円 * ${
              reservingTrip.peopleNum
            }名`}</p>
          </div>
        ) : (
          bufferItems.map(item => (
            <div key={item.key} style={styles.item}>
              <p>{item.title}</p>
              <p>{item.bufferAmountText}</p>
            </div>
          ))
        )}
      </div>
    </ReserveConfirmOutlineBox>
  );
};

export default BufferAmountsSection;
